import axiosClient from "./axios-client/axiosClient.jsx";

/* ReceivablesAPI — M1 client per RECEIVABLES_M1_FRONTEND_GUIDE.md.

   M1 ships four CRUD endpoints behind the receivables_v1 feature
   flag. When the flag is off, every call returns 404 — treat that
   as "feature unavailable for this user" and hide the tab.

   Mutating endpoints (create, consolidate) require an
   Idempotency-Key header. Generated per logical operation, reused
   on network retries within the same operation.

   All response codes follow the existing { code, message } pattern.
   code === 0 means success; code !== 0 is an application-level
   error to handle even on 200 OK.
*/

const idempotencyKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  /* Fallback for older browsers / non-secure contexts. RFC4122v4-ish. */
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

class ReceivablesAPI {
  /* List channels. status filter accepts "active" | "paused" | "archived";
     server defaults to "active" when omitted. M1 only allows "active"
     channels (pause/archive ships in M2). */
  listChannels(token, { status, page = 0, size = 20 } = {}) {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    params.set("page", String(page));
    params.set("size", String(size));
    return axiosClient.get(`/receivables/channels?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /* Cheap probe for the receivables_v1 feature flag. Returns the
     same response as listChannels but with size=1. Caller treats
     a 404 (any) as "flag is off" and hides the tab. */
  probe(token) {
    return this.listChannels(token, { size: 1 });
  }

  /* Create channel. Idempotency-Key is generated here; the caller
     should NOT regenerate it on retry — pass the same key back via
     the second `key` argument when retrying a failed request. */
  createChannel(token, payload, key) {
    return axiosClient.post("/receivables/channels", payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Idempotency-Key": key || idempotencyKey(),
        "Content-Type": "application/json",
      },
    });
  }

  /* Read a single channel. Backend returns 404 if the channel
     belongs to a different user; treat as "navigated to a stale
     bookmark" and redirect with a toast. */
  getChannel(token, channelId) {
    return axiosClient.get(`/receivables/channels/${channelId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /* List payments for a channel. M1 always returns an empty array
     (ingestion ships in M2). The since parameter is ISO-8601;
     malformed → 400 code 1001. */
  listChannelPayments(token, channelId, { since, page = 0, size = 50 } = {}) {
    const params = new URLSearchParams();
    if (since) params.set("since", since);
    params.set("page", String(page));
    params.set("size", String(size));
    return axiosClient.get(
      `/receivables/channels/${channelId}/payments?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  /* Consolidate. With M2.5 backed by a real receiving account this
     can now return code 0 with a real breakdown when balance > 0.
     Code 2001 still means "no balance yet to sweep". */
  consolidate(token, channelId, { toCurrency, amount }, key) {
    const body = { to_currency: toCurrency };
    if (amount != null && amount !== "") body.amount = String(amount);
    return axiosClient.post(
      `/receivables/channels/${channelId}/consolidate`,
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Idempotency-Key": key || idempotencyKey(),
          "Content-Type": "application/json",
        },
      },
    );
  }

  /* Update channel — pause/archive/rename + auto_consolidate toggle.
     The verb is POST not PATCH because of a Micronaut binding bug
     in the backend's environment; semantics are PATCH (partial
     update — only supplied fields are touched). Documented loudly. */
  updateChannel(token, channelId, fields, key) {
    return axiosClient.post(
      `/receivables/channels/${channelId}/update`,
      fields,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Idempotency-Key": key || idempotencyKey(),
          "Content-Type": "application/json",
        },
      },
    );
  }

  /* Summary KPIs for the Receivables tab header — M2.5 endpoint.
     Replaces the client-side aggregation of channel objects with
     authoritative server-side totals (handles archived/paused
     channels correctly, includes by-currency and top-channel
     breakdowns). */
  getSummary(token, { since } = {}) {
    const params = new URLSearchParams();
    if (since) params.set("since", since);
    const qs = params.toString();
    return axiosClient.get(
      `/receivables/summary${qs ? `?${qs}` : ""}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  /* List webhook subscriptions for this user. The signing_secret
     is masked in this response — only the POST creation response
     returns it in the clear, exactly once. */
  listWebhooks(token) {
    return axiosClient.get(`/receivables/webhooks`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /* Create a webhook subscription. The response includes
     signing_secret in plaintext on this call ONLY — subsequent
     GETs return it masked. UI must surface a "Copy and store this
     secret now; it cannot be retrieved later" pattern. */
  createWebhook(token, { url, events }, key) {
    return axiosClient.post(
      `/receivables/webhooks`,
      { url, events },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Idempotency-Key": key || idempotencyKey(),
          "Content-Type": "application/json",
        },
      },
    );
  }

  deleteWebhook(token, webhookId) {
    return axiosClient.post(`/receivables/webhooks/${webhookId}/delete`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /* Per-subscription delivery history. Returns an array of attempts
     with status (pending / delivered / failed / dead_lettered),
     attempt_count, the last response status + body excerpt (truncated
     to 1000 chars by the worker), and timing fields. Default page
     size 50, max 200. */
  listWebhookDeliveries(token, webhookId, limit) {
    const qs = limit ? `?limit=${encodeURIComponent(limit)}` : "";
    return axiosClient.get(`/receivables/webhooks/${webhookId}/deliveries${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }
}

export const receivablesAPI = new ReceivablesAPI();
export const newIdempotencyKey = idempotencyKey;
